import * as Sentry from '@sentry/nextjs';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import Button from 'components/buttons/button';

import Anchor from 'components/typography/anchor';
import { useAnalytics } from 'hooks/useAnalytics';
import useMediaQuery from 'hooks/useMediaQuery';

import { convertCentsToPromoteQuantity } from 'lib/api/premium';
import getStripe from 'lib/getStripe';
import {
  getPromoteTiers,
  getStripeAppearanceConfig,
  getStripeFontConfig,
} from 'lib/stripe';
import { useEffect, useState } from 'react';

const stripePromise = getStripe();

const DROPDOWN_OPTIONS_CENTS = getPromoteTiers();
const DEFAULT_DROPDOWN_VALUE_CENTS = DROPDOWN_OPTIONS_CENTS[2]; // 1000

const PromoteContent = ({ shareCode, type }) => {
  // Get stripe form apprearance config
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const appearanceConfig = getStripeAppearanceConfig({ darkMode: prefersDark });
  const fontConfig = getStripeFontConfig();

  const elementsOptions = {
    mode: 'payment',
    amount: DEFAULT_DROPDOWN_VALUE_CENTS,
    currency: 'usd',
    appearance: appearanceConfig,
    fonts: fontConfig,
  };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <CheckoutForm shareCode={shareCode} type={type} />
    </Elements>
  );
};

const CheckoutForm = ({ shareCode, type }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { donationTrackEvent } = useAnalytics();

  const [selectedPromoteCents, setSelectedPromoteCents] = useState(
    DEFAULT_DROPDOWN_VALUE_CENTS
  );
  const [canSubmit, setCanSubmit] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [email, setEmail] = useState('');

  const handlePromoteQuantityChange = (e) => {
    const parsedValue =
      parseInt(e.target.value, 10) || DEFAULT_DROPDOWN_VALUE_CENTS;
    setSelectedPromoteCents(parsedValue);
    elements.update({ amount: parsedValue });
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // This triggers the PaymentElement's validation
    const { error: submitError } = await elements.submit();

    if (submitError) {
      // This is a failure of the Stripe form to validate itself.
      setErrorMessage(
        'Please correct the indicated card errors and try again.'
      );
      setIsLoading(false);
      return;
    }

    try {
      const paymentIntentParams = {
        cents: selectedPromoteCents,
        promoteType: type,
        shareCode,
        email,
      };

      const response = await fetch('/api/payments/promote/get-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentIntentParams),
      });
      const { clientSecret, error: paymentIntentError } = await response.json();

      // Check the status code of response
      if (response.status === 400) {
        // Validation Failed
        setErrorMessage(paymentIntentError.message);
        setIsLoading(false);
        return;
      } else if (paymentIntentError) {
        // Something worse, 404, 500, etc.
        setErrorMessage(paymentIntentError.message);
        setIsLoading(false);
        return;
      }

      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${process.env.SITE_URL}/account/thanks`,
        },
      });
      if (error) {
        // Sentry BreadCrumb
        Sentry.addBreadcrumb({
          category: 'stripe:confirmPayment',
          message: 'Error confirming payment',
          data: error,
        });
        setErrorMessage(
          `There was an error finalizing your payment with Stripe: ${error.message}`
        );
        // Send to Sentry
        Sentry.captureException(error);
        setIsLoading(false);
        return;
      }
      donationTrackEvent({
        donationValueCents: selectedPromoteCents,
      });
      setIsLoading(false);
    } catch (error) {
      // A 500's can be messy here if they're returning HTML and not JSON
      const errorString = error.toString();
      setErrorMessage(errorString);
      setIsLoading(false);
      Sentry.captureException(error);
    }
    setIsLoading(false);
  };

  // Form Validation and Submission Controls
  useEffect(() => {
    // Have them type at least a few email chars before it loads.
    // Later could debounce and check it against HIBP etc.
    if (stripe && elements && selectedPromoteCents > 0 && email.length > 4) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [stripe, elements, selectedPromoteCents, email]);

  return (
    <Sentry.ErrorBoundary
      fallback={<p>Promote this by sending PROMOTE {shareCode} to 50409!</p>}
    >
      <div className="mt-8 card-flat flex-col border-3 border-pink-700 dark:border-amber-400 print:hidden">
        <h2>Fund texts of this {type.charAt(0) + type.slice(1)}</h2>
        <p className="mb-4">
          Drive more letter deliveries by funding text appeals to users.{' '}
          <Anchor href="/premium" className="anchor-text">
            Become a member
          </Anchor>{' '}
          to double your reach per&nbsp;dollar.
        </p>

        <form onSubmit={handleSubmitPayment} id="promote-form">
          <div className="flex flex-col gap-4 sm:flex-row">
            {/* Email */}
            <fieldset className="sm:basis-1/2">
              <label htmlFor="email" className="block">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 form-input h-10"
              />
            </fieldset>

            {/* Quantity */}
            <fieldset className="sm:basis-1/2">
              <label htmlFor="quantity" className="block">
                Amount to Spend
              </label>
              <select
                id={'quantity'}
                value={selectedPromoteCents}
                onChange={handlePromoteQuantityChange}
                className="mt-1 form-input h-10"
              >
                <option value="0">How many people?</option>
                {DROPDOWN_OPTIONS_CENTS.map((cents) => (
                  <option value={cents} key={cents}>
                    ${cents / 100} ~ (
                    {convertCentsToPromoteQuantity(cents).toLocaleString()}{' '}
                    estimated reach)
                  </option>
                ))}
              </select>
            </fieldset>
          </div>

          {/* Payment Method Entry */}
          {canSubmit ? (
            <div className="mt-4">
              <PaymentElement />{' '}
              <Button
                theme="blue"
                disabled={!canSubmit}
                type="submit"
                className="mt-4"
              >
                Pay and Promote to ~
                {convertCentsToPromoteQuantity(
                  selectedPromoteCents
                ).toLocaleString()}{' '}
                people
              </Button>
              {/* Error and Status Info */}
              {isLoading && <div>Sumbitting payment...</div>}
              {errorMessage && (
                <div className="mt-4 rounded-sm bg-red-600 p-2 text-white">
                  {errorMessage}
                </div>
              )}
            </div>
          ) : null}
        </form>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default PromoteContent;
